'use client';

import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import WhatsAppFloat from '@/components/WhatsAppFloat';

import {
  ArrowRight,
  CheckCircle2,
  Clock3,
  MapPin,
  Plane,
  ShieldCheck,
  Star,
  Users,
  BriefcaseBusiness,
  Car,
  Route,
  CalendarCheck,
  MessageCircle,
  ChevronDown,
  Building2,
  Compass,
  BookOpenText,
  KeyRound,
} from 'lucide-react';

const WA_NUMBER = '6287786556067';

const createWhatsAppUrl = (vehicle = '') => {
  const message = `Halo NAYVERA INDO TRANSPORT,

Saya ingin menanyakan ketersediaan sewa mobil Bekasi.

DATA PEMESANAN

Nama pemesan:
Kendaraan yang diminati: ${vehicle}
Jenis sewa: Dengan Sopir / Lepas Kunci
Tanggal mulai:
Jam mulai:
Tanggal selesai:
Jam selesai:
Lokasi penjemputan:
Tujuan / rute:
Jumlah penumpang:

Mohon informasi mengenai:
• Ketersediaan kendaraan
• Harga dan pilihan paket
• Fasilitas yang didapat
• Syarat dan ketentuan
• Biaya tambahan jika ada

Terima kasih.`;

  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`;
};

const fleet = [
  {
    name: 'Toyota Agya / Daihatsu Ayla',
    category: 'City Car',
    status: 'Tersedia',
    transmission: 'Matic / Manual',
    fuel: 'Bensin',
    capacity: '5 Penumpang',
    image: '/assets/images/AYLA DAN AGYA.jpg',
    features: ['Power Steering', 'AC Dingin', 'Audio Bluetooth', 'Eco Mode'],
    price: 'Rp 298.000',
    priceLabel: '12 jam · lepas kunci',
    description: 'Pilihan praktis untuk mobilitas harian, melewati jalanan perkotaan, dan aktivitas ringkas di Bekasi.',
  },
  {
    name: 'Toyota Avanza',
    category: 'Family MPV',
    status: 'Tersedia',
    transmission: 'Matic / Manual',
    fuel: 'Bensin',
    capacity: '7 Penumpang',
    image: '/assets/images/Avanza.jpeg',
    features: ['AC Double Blower', 'Audio Sistem', 'Kabin Luas', '+1 lagi'],
    price: 'Rp 550.000',
    priceLabel: '12 jam · lepas kunci',
    description: 'Andalan kendaraan keluarga untuk perjalanan dalam kota, silaturahmi, maupun mobilitas harian.',
  },
  {
    name: 'Toyota Innova Reborn',
    category: 'Family & Business',
    status: 'Tersedia',
    transmission: 'Matic',
    fuel: 'Diesel / Bensin',
    capacity: '7 Penumpang',
    image: '/assets/images/REBORN.jpeg',
    features: ['AC Double Blower', 'Kabin Nyaman', 'Captain Seat opt', '+1 lagi'],
    price: 'Rp 700.000',
    priceLabel: '12 jam · lepas kunci',
    description: 'Cocok untuk perjalanan keluarga besar, kunjungan bisnis korporat, dan rute luar kota dari Bekasi.',
  },
  {
    name: 'Toyota Innova Zenix',
    category: 'Executive MPV',
    status: 'Tersedia',
    transmission: 'Matic',
    fuel: 'Bensin / Hybrid',
    capacity: '7 Penumpang',
    image: '/assets/images/ZENIX G.webp',
    features: ['Panoramic Roof', 'Captain Seat', 'Electric Parking', '+1 lagi'],
    price: 'Rp 850.000',
    priceLabel: '12 jam · lepas kunci',
    description: 'Alternatif kendaraan modern berteknologi tinggi untuk kenyamanan maksimal perjalanan keluarga maupun bisnis.',
  },
  {
    name: 'Toyota Fortuner',
    category: 'Premium SUV',
    status: 'Tersedia',
    transmission: 'Matic',
    fuel: 'Diesel',
    capacity: '7 Penumpang',
    image: '/assets/images/Fortuner.jpeg',
    features: ['4x2 / 4x4', 'Leather Seat', 'Push Start Button', '+1 lagi'],
    price: 'Rp 1.600.000',
    priceLabel: '12 jam · dengan driver',
    description: 'SUV tangguh dan elegan untuk keperluan tamu VIP, agenda khusus, serta perjalanan lintas kota.',
  },
  {
    name: 'Toyota Hiace Premio',
    category: 'Premium Van',
    status: 'Tersedia',
    transmission: 'Manual',
    fuel: 'Diesel',
    capacity: '14 Penumpang',
    image: '/assets/images/Hiace-Premio.jpeg',
    features: ['High Roof', 'AC per Kepala', 'Reclining Seat', '+1 lagi'],
    price: 'Rp 1.800.000',
    priceLabel: '12 jam · dengan driver',
    description: 'Kabin lapang berkapasitas besar untuk rombongan kantor, event perusahaan, maupun rekreasi keluarga.',
  },
];

const services = [
  {
    icon: KeyRound,
    title: 'Sewa Mobil Lepas Kunci Bekasi',
    description:
      'Solusi mandiri bagi Anda yang ingin berkendara sendiri di wilayah Bekasi dan sekitarnya. Syarat verifikasi mudah dan unit terawat.',
  },
  {
    icon: Users,
    title: 'Sewa Mobil dengan Driver',
    description:
      'Perjalanan bebas lelah ditemani pengemudi berpengalaman yang menguasai jalur perkotaan padat hingga rute tol trans-jawa.',
  },
  {
    icon: Plane,
    title: 'Antar Jemput Bandara',
    description:
      'Layanan penjemputan tepat waktu dari Bekasi menuju Bandara Soekarno-Hatta atau Halim Perdanakusuma.',
  },
  {
    icon: BriefcaseBusiness,
    title: 'Transportasi Korporat & Industri',
    description:
      'Mendukung mobilitas kawasan industri (Cikarang, MM2100, Jababeka) untuk keperluan meeting, audit, atau antar jemput tamu pabrik.',
  },
  {
    icon: Route,
    title: 'Carter Luar Kota dari Bekasi',
    description:
      'Layanan perjalanan jarak jauh aman dan nyaman berangkat langsung dari Bekasi menuju berbagai kota di Jawa dan Sumatra.',
  },
  {
    icon: Compass,
    title: 'Wisata & Acara Keluarga',
    description:
      'Armada prima untuk mengantar rombongan keluarga berlibur atau menghadiri hajatan di dalam maupun luar kota.',
  },
];

const useCases = [
  {
    icon: CalendarCheck,
    title: 'Mobilitas Harian Perkotaan',
    description:
      'Untuk menunjang aktivitas harian, urusan pekerjaan, maupun mobilitas rutin di kawasan Bekasi.',
  },
  {
    icon: Building2,
    title: 'Kawasan Industri & Bisnis',
    description:
      'Solusi transportasi profesional untuk korporasi di area industri Cikarang dan sekitarnya.',
  },
  {
    icon: Plane,
    title: 'Perjalanan Bandara',
    description:
      'Layanan antar jemput bandara praktis tanpa perlu pusing memikirkan jadwal taksi atau transportasi umum.',
  },
  {
    icon: Users,
    title: 'Rombongan & Keluarga',
    description:
      'Pilihan mobil MPV hingga Van besar untuk menampung seluruh anggota keluarga dalam satu kendaraan.',
  },
  {
    icon: Star,
    title: 'Acara & Pernikahan',
    description:
      'Kendaraan bersih dan berkelas untuk menunjang berbagai acara spesial serta resepsi pernikahan.',
  },
  {
    icon: Route,
    title: 'Perjalanan Lintas Provinsi',
    description:
      'Siap mengantar perjalanan jauh dari Bekasi via tol maupun jalur arteri dengan aman.',
  },
];

const areaGroups = [
  {
    title: 'Bekasi Kota',
    description: 'Cakupan cepat di pusat perkotaan.',
    places: [
      'Bekasi Selatan (Grand Galaxy)',
      'Bekasi Barat (Summarecon)',
      'Bekasi Timur',
      'Bekasi Utara (Harapan Indah)',
      'Medansatria',
      'Jatiasih',
    ],
  },
  {
    title: 'Bekasi Kabupaten & Industri',
    description: 'Melayani area pabrik dan hunian mandiri.',
    places: [
      'Cikarang Pusat & Utara',
      'Jababeka',
      'Kawasan Industri MM2100',
      'Tambun',
      'Cibitung',
      'Tarumajaya',
    ],
  },
  {
    title: 'Akses Tol & Perbatasan',
    description: 'Titik strategis keberangkatan.',
    places: [
      'Stasiun Bekasi',
      'Gerbang Tol Bekasi Barat',
      'Gerbang Tol Bekasi Timur',
      'Jatibening',
      'Pondok Gede',
      'Kranji',
    ],
  },
  {
    title: 'Bandara & Luar Kota',
    description: 'Aksesibilitas perjalanan jarak jauh.',
    places: [
      'Bandara Soekarno-Hatta',
      'Bandara Halim Perdanakusuma',
      'Karawang',
      'Bogor & Depok',
      'Bandung',
      'Jalur Tol Jakarta-Cikampek',
    ],
  },
];

const reasons = [
  {
    icon: Car,
    title: 'Unit Terawat & Prima',
    text: 'Perawatan rutin berkala untuk memastikan performa mesin selalu optimal dan nyaman dikendarai.',
  },
  {
    icon: MessageCircle,
    title: 'Respon Cepat WhatsApp',
    text: 'Pemesanan praktis dan konsultasi langsung melalui admin kami yang siap melayani 24 jam.',
  },
  {
    icon: MapPin,
    title: 'Paham Rute Jabodetabek',
    text: 'Pengemudi handal yang menguasai jalanan Bekasi, Jakarta, serta rute tol alternatif.',
  },
  {
    icon: ShieldCheck,
    title: 'Transparan & Amanah',
    text: 'Kejelasan informasi harga, ketentuan sewa, dan fasilitas tanpa ada biaya tersembunyi.',
  },
];

const articles = [
  {
    number: '01',
    category: 'Panduan Sewa',
    title: 'Tips Memilih Rental Mobil Bekasi untuk Kebutuhan Harian dan Bisnis',
    paragraphs: [
      'Bekasi merupakan salah satu kota penyangga dengan tingkat mobilitas yang sangat tinggi, baik untuk urusan pekerjaan ke Jakarta maupun aktivitas bisnis di kawasan industri Cikarang. Memilih layanan rental mobil yang tepat akan sangat menentukan efisiensi waktu perjalanan Anda.',
      'Jika Anda sering berpindah tempat di dalam kota melalui jalanan padat, penggunaan city car atau MPV kompak seperti Avanza adalah pilihan rasional. Namun, untuk kunjungan korporat atau menjamu tamu penting, unit premium memberikan citra yang lebih profesional.',
      'Pastikan selalu mengonfirmasi durasi sewa, batas wilayah pemakaian, serta ketentuan bahan bakar kepada penyedia jasa sebelum menyetujui pemesanan.',
    ],
  },
  {
    number: '02',
    category: 'Korporat Industri',
    title: 'Solusi Transportasi Kawasan Industri Cikarang dan MM2100 bersama NAYVERA',
    paragraphs: [
      'Kawasan industri besar seperti Jababeka, EJIP, Delta Silicon, dan MM2100 di Kabupaten Bekasi menuntut mobilitas operasional yang cepat dan tepat waktu bagi para pelaku bisnis maupun ekspatriat.',
      'NAYVERA INDO TRANSPORT menyediakan layanan sewa mobil jangka pendek maupun korporat untuk mendukung kunjungan pabrik, audit, hingga transportasi harian staf manajemen.',
      'Dengan dukungan pengemudi profesional yang disiplin waktu, urusan mobilitas bisnis di kawasan industri industri dapat berjalan lancar tanpa hambatan.',
    ],
  },
  {
    number: '03',
    category: 'Lepas Kunci vs Sopir',
    title: 'Mana yang Lebih Efektif di Bekasi: Lepas Kunci atau Menggunakan Sopir?',
    paragraphs: [
      'Memilih opsi lepas kunci memberikan keleluasaan penuh bagi Anda yang ingin mengatur jadwal perjalanan secara mandiri bersama keluarga di Bekasi. Namun, syarat verifikasi dokumen harus terpenuhi demi keamanan bersama.',
      'Sebaliknya, menyewa mobil dengan sopir jauh lebih disarankan jika Anda harus menghadapi kemacetan jam sibuk menuju Jakarta atau belum hafal rute jalan tikus di area Bekasi dan sekitarnya.',
      'Pilihlah jenis layanan yang paling selaras dengan agenda dan kondisi fisik Anda agar perjalanan tetap terasa menyenangkan.',
    ],
  },
  {
    number: '04',
    category: 'Airport Transfer',
    title: 'Panduan Praktis Antar Jemput Bandara dari Bekasi (Soetta & Halim)',
    paragraphs: [
      'Perjalanan dari Bekasi menuju Bandara Soekarno-Hatta atau Bandara Halim Perdanakusuma memerlukan perhitungan waktu yang cermat karena dipengaruhi oleh kepadatan arus lalu lintas di ruas Tol Jakarta-Cikampek.',
      'Memesan layanan antar jemput bandara khusus dari NAYVERA menghindarkan Anda dari kerepotan membawa barang bawaan berat berpindah-pindah moda transportasi umum.',
      'Informasikan nomor penerbangan dan jam keberangkatan Anda kepada admin kami agar penjemputan dapat dijadwalkan secara presisi.',
    ],
  },
  {
    number: '05',
    category: 'Rekomendasi Rombongan',
    title: 'Sewa Hiace di Bekasi untuk Keperluan Wisata dan Rombongan Kantor',
    paragraphs: [
      'Mengadakan agenda rekreasi kantor atau perjalanan wisata keluarga besar dari Bekasi akan jauh lebih seru jika menggunakan satu kendaraan besar seperti Toyota Hiace.',
      'Kabin yang lega dilengkapi AC dingin merata serta kursi yang dapat direbahkan membuat perjalanan jauh terasa lebih santai tanpa rasa pegal.',
      'Diskusikan jumlah peserta rombongan Anda dengan tim kami agar kapasitas kendaraan dipersiapkan secara maksimal.',
    ],
  },
  {
    number: '06',
    category: 'Tips Booking',
    title: 'Langkah Mudah Melakukan Pemesanan Rental Mobil di Bekasi',
    paragraphs: [
      'Proses booking di NAYVERA dibuat sangat praktis melalui WhatsApp tanpa prosedur formulir website yang merepotkan.',
      'Cukup sampaikan informasi tanggal mulai, durasi, titik penjemputan di area Bekasi, tujuan, serta jenis armada yang Anda minati.',
      'Tim admin kami akan segera mengonfirmasi ketersediaan unit beserta rincian biaya secara transparan sebelum perjalanan Anda dimulai.',
    ],
  },
];

const faqs = [
  {
    question: 'Berapa kisaran harga sewa mobil di Bekasi bersama NAYVERA?',
    answer:
      'Tarif sewa sangat kompetitif dan bervariasi mulai dari 300 ribuan tergantung pada jenis kendaraan, durasi pemakaian, serta pilihan layanan lepas kunci atau dengan sopir.',
  },
  {
    question: 'Apakah tersedia layanan rental mobil lepas kunci di Bekasi?',
    answer:
      'Ya, kami menyediakan opsi lepas kunci untuk unit tertentu dengan melampirkan beberapa syarat dokumen verifikasi identitas yang berlaku.',
  },
  {
    question: 'Bagaimana cara memesan mobil dengan pengemudi (driver)?',
    answer:
      'Anda tinggal memilih armada yang diinginkan dan mengonfirmasikan kebutuhan driver kepada admin kami via WhatsApp. Driver kami ramah dan sangat menguasai rute Jabodetabek.',
  },
  {
    question: 'Wilayah mana saja di Bekasi yang dijangkau oleh layanan ini?',
    answer:
      'Kami mencakup seluruh wilayah Bekasi Kota, Bekasi Kabupaten, kawasan industri Cikarang, Tambun, Cibitung, hingga perbatasan Jakarta.',
  },
  {
    question: 'Apakah melayani penjemputan menuju Bandara Soetta atau Halim?',
    answer:
      'Tentu saja. Kami melayani rute drop-off maupun pick-up dari wilayah Bekasi menuju Bandara Soekarno-Hatta maupun Bandara Halim Perdanakusuma secara tepat waktu.',
  },
  {
    question: 'Apakah kendaraan bisa disewa untuk perjalanan ke luar kota?',
    answer:
      'Bisa. Anda dapat menggunakan armada kami untuk perjalanan ke Bandung, Bogor, Cirebon, Jawa Tengah, atau kota lainnya sesuai kesepakatan rute.',
  },
  {
    question: 'Bagaimana prosedur konfirmasi pemesanan?',
    answer:
      'Hubungi kami melalui tombol WhatsApp, kirimkan format data pemesanan, dan admin akan segera memberikan rincian ketersediaan unit serta harganya.',
  },
  {
    question: 'Apakah harga sewa sudah termasuk tol, parkir, dan bensin?',
    answer:
      'Komponen biaya bergantung pada jenis paket yang Anda ambil. Rincian apakah harga sudah mencakup BBM, tol, dan parkir akan dijelaskan secara transparan oleh admin.',
  },
];

export default function SewaMobilBekasiPage() {
  const whatsappUrl = createWhatsAppUrl();

  return (
    <main className="min-h-screen bg-white text-slate-900">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#07111f]">
        <div className="absolute inset-0">
          <img
            src="/assets/images/AYLA DAN AGYA.jpg"
            alt="Sewa mobil Bekasi NAYVERA INDO TRANSPORT"
            className="h-full w-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#07111f] via-[#07111f]/95 to-[#07111f]/55" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#07111f] via-transparent to-transparent" />
        </div>

        <div className="relative mx-auto grid min-h-[680px] max-w-7xl items-center gap-12 px-6 py-20 lg:grid-cols-[1.15fr_.85fr] lg:px-8">
          <div className="max-w-3xl">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#d4af37]/40 bg-[#d4af37]/10 px-4 py-2 text-sm font-medium text-[#e4c45a]">
              <MapPin size={16} />
              Sewa Mobil Bekasi & Sekitarnya
            </div>

            <h1 className="text-4xl font-bold leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
              Sewa Mobil Bekasi
              <span className="mt-2 block text-[#d4af37]">
                Mulai dari 300 Ribuan
              </span>
            </h1>

            <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-300">
              NAYVERA INDO TRANSPORT menyediakan solusi rental mobil profesional
              di Bekasi untuk kebutuhan harian, mobilitas kawasan industri Cikarang,
              keluarga, antar-jemput bandara, hingga carter luar kota.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#d4af37] px-6 py-3.5 font-semibold text-[#07111f] transition hover:bg-[#e4c45a]"
              >
                <MessageCircle size={19} />
                Konsultasi & Booking WhatsApp
                <ArrowRight size={18} />
              </a>

              <a
                href="#armada"
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/20 bg-white/5 px-6 py-3.5 font-semibold text-white backdrop-blur transition hover:border-[#d4af37]/60 hover:bg-white/10"
              >
                Lihat Pilihan Armada
              </a>
            </div>

            <div className="mt-10 grid max-w-3xl grid-cols-2 gap-4 sm:grid-cols-4">
              {[
                ['300 Ribuan', 'Tarif terjangkau*'],
                ['Lepas Kunci', 'Opsi fleksibel'],
                ['Dengan Sopir', 'Driver berpengalaman'],
                ['Bekasi & Cikarang', 'Cakupan luas'],
              ].map(([value, label]) => (
                <div
                  key={label}
                  className="border-l border-[#d4af37]/50 pl-4"
                >
                  <div className="font-bold text-[#e4c45a]">{value}</div>
                  <div className="mt-1 text-xs leading-5 text-slate-400">
                    {label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="hidden lg:block">
            <div className="rounded-3xl border border-white/10 bg-white/[0.06] p-7 shadow-2xl backdrop-blur">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#d4af37]/15 text-[#e4c45a]">
                  <Car size={25} />
                </div>

                <div>
                  <p className="font-bold text-white">Rencana Perjalanan di Bekasi?</p>
                  <p className="text-sm text-slate-400">
                    Konsultasikan kebutuhan Anda sekarang
                  </p>
                </div>
              </div>

              <div className="mt-7 space-y-4">
                {[
                  'Tentukan tanggal dan durasi sewa',
                  'Pilih armada sesuai jumlah penumpang',
                  'Tentukan titik jemput di Bekasi',
                  'Konfirmasikan harga & fasilitas',
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <CheckCircle2
                      size={19}
                      className="mt-0.5 shrink-0 text-[#d4af37]"
                    />
                    <p className="text-sm leading-6 text-slate-300">
                      {item}
                    </p>
                  </div>
                ))}
              </div>

              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#d4af37] px-5 py-3.5 font-bold text-[#07111f] transition hover:bg-[#e4c45a]"
              >
                <MessageCircle size={18} />
                Tanyakan Ketersediaan Unit
              </a>

              <p className="mt-4 text-center text-xs leading-5 text-slate-400">
                Respon cepat via WhatsApp oleh tim NAYVERA.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="bg-white py-20">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-[1.1fr_.9fr] lg:px-8">
          <div>
            <p className="mb-3 text-sm font-bold uppercase tracking-[0.2em] text-[#b89528]">
              Pusat Rental Mobil Bekasi
            </p>

            <h2 className="text-3xl font-bold leading-tight text-[#07111f] sm:text-4xl">
              Solusi Transportasi Terbaik di
              <span className="text-[#b89528]"> Kota & Kabupaten Bekasi</span>
            </h2>

            <p className="mt-6 text-base leading-8 text-slate-600">
              Membutuhkan layanan sewa mobil yang terpercaya, bersih, dan on-time di Bekasi? 
              NAYVERA INDO TRANSPORT hadir memenuhi kebutuhan mobilitas masyarakat perkotaan, 
              keluarga, hingga keperluan operasional korporat di kawasan industri Cikarang.
            </p>

            <p className="mt-4 text-base leading-8 text-slate-600">
              Kami menyediakan fleksibilitas penuh melalui pilihan sewa mobil lepas kunci 
              bagi yang ingin berkendara mandiri, maupun layanan dengan sopir profesional 
              yang siap mengantar perjalanan Anda tanpa rasa lelah.
            </p>
          </div>

          <div className="rounded-3xl bg-[#07111f] p-8 shadow-xl">
            <div className="mb-7 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#d4af37]/15 text-[#e4c45a]">
              <ShieldCheck size={25} />
            </div>

            <h3 className="text-2xl font-bold text-white">
              Keunggulan Layanan NAYVERA
            </h3>

            <div className="mt-7 space-y-4">
              {[
                'Armada prima dan rutin perawatan',
                'Opsi lepas kunci atau dengan sopir',
                'Cakupan luas Bekasi & Cikarang',
                'Transparan tanpa biaya tersembunyi',
                'Layanan konsultasi ramah 24 jam',
              ].map((item) => (
                <div key={item} className="flex gap-3">
                  <CheckCircle2
                    size={20}
                    className="mt-0.5 shrink-0 text-[#d4af37]"
                  />
                  <span className="text-sm leading-6 text-slate-300">
                    {item}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-[#07111f] py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="mb-3 text-sm font-bold uppercase tracking-[0.2em] text-[#e4c45a]">
              Layanan NAYVERA di Bekasi
            </p>

            <h2 className="text-3xl font-bold text-white sm:text-4xl">
              Pilihan Jenis Layanan Rental Mobil
              <span className="text-[#d4af37]"> Sesuai Kebutuhan</span>
            </h2>

            <p className="mt-5 leading-8 text-slate-400">
              Pilih kategori layanan yang paling pas untuk mendukung agenda harian maupun bisnis Anda di Bekasi.
            </p>
          </div>

          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {services.map((service) => {
              const Icon = service.icon;

              return (
                <article
                  key={service.title}
                  className="group rounded-2xl border border-white/10 bg-white/[0.04] p-6 transition duration-300 hover:-translate-y-1 hover:border-[#d4af37]/60 hover:bg-white/[0.07]"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#d4af37]/10 text-[#e4c45a] transition group-hover:bg-[#d4af37] group-hover:text-[#07111f]">
                    <Icon size={23} />
                  </div>

                  <h3 className="mt-6 text-xl font-bold text-white">
                    {service.title}
                  </h3>

                  <p className="mt-3 text-sm leading-7 text-slate-400">
                    {service.description}
                  </p>

                  <a
                    href={createWhatsAppUrl(service.title)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#e4c45a] transition hover:text-white"
                  >
                    Konsultasikan layanan ini
                    <ArrowRight size={16} />
                  </a>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <section className="bg-slate-50 py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#b89528]">
              Agenda Perjalanan
            </p>

            <h2 className="mt-3 text-3xl font-bold text-[#07111f] sm:text-4xl">
              Kendaraan Andal untuk
              <span className="text-[#b89528]"> Berbagai Keperluan</span>
            </h2>
          </div>

          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {useCases.map((item) => {
              const Icon = item.icon;

              return (
                <article
                  key={item.title}
                  className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:border-[#d4af37]/50 hover:shadow-lg"
                >
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#07111f] text-[#e4c45a]">
                    <Icon size={21} />
                  </div>

                  <h3 className="mt-5 text-lg font-bold text-[#07111f]">
                    {item.title}
                  </h3>

                  <p className="mt-3 text-sm leading-7 text-slate-600">
                    {item.description}
                  </p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* FLEET / ARMADA (Desain Baru Sesuai Referensi) */}
      <section id="armada" className="bg-white py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
            <div className="max-w-3xl">
              <p className="mb-3 text-sm font-bold uppercase tracking-[0.2em] text-[#b89528]">
                Katalog Armada
              </p>

              <h2 className="text-3xl font-bold text-[#07111f] sm:text-4xl">
                Pilihan Armada Sewa Mobil
                <span className="text-[#b89528]"> Bekasi</span>
              </h2>

              <p className="mt-5 leading-8 text-slate-600">
                Berikut adalah deretan armada terbaik kami yang siap disewa untuk menemani 
                perjalanan Anda di Bekasi, Cikarang, dan sekitarnya.
              </p>
            </div>

            <a
              href="/fleet-armada-page"
              className="inline-flex items-center gap-2 font-semibold text-[#07111f] transition hover:text-[#b89528]"
            >
              Lihat Armada Lengkap
              <ArrowRight size={18} />
            </a>
          </div>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {fleet.map((car) => (
              <article
                key={car.name}
                className="group flex flex-col justify-between overflow-hidden rounded-3xl border border-slate-200 bg-white p-5 shadow-sm transition duration-300 hover:-translate-y-1 hover:border-[#d4af37]/70 hover:shadow-xl"
              >
                <div>
                  <div className="relative aspect-[16/10] overflow-hidden rounded-2xl bg-slate-100">
                    <img
                      src={car.image}
                      alt={`${car.name} untuk sewa mobil Bekasi NAYVERA`}
                      className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                    />

                    <div className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-500 px-3 py-1 text-xs font-bold text-white shadow-sm">
                      <span className="h-2 w-2 rounded-full bg-white animate-pulse" />
                      {car.status}
                    </div>

                    <div className="absolute right-3 top-3 rounded-full bg-[#d4af37] px-3 py-1 text-xs font-bold text-[#07111f] shadow-sm">
                      {car.category}
                    </div>

                    <div className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 rounded-lg bg-black/60 px-2.5 py-1 text-xs font-semibold text-white backdrop-blur-sm">
                      <Users size={13} className="text-[#d4af37]" />
                      {car.capacity}
                    </div>
                  </div>

                  <p className="mt-4 text-xs font-bold uppercase tracking-wider text-[#b89528]">
                    {car.category}
                  </p>

                  <h3 className="mt-1 text-xl font-bold text-[#07111f]">
                    {car.name}
                  </h3>

                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="inline-flex items-center gap-1.5 rounded-xl bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700">
                      ⚙️ {car.transmission}
                    </span>
                    <span className="inline-flex items-center gap-1.5 rounded-xl bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700">
                      ⛽ {car.fuel}
                    </span>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {car.features.map((feature, idx) => (
                      <span
                        key={idx}
                        className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs text-slate-600"
                      >
                        ✨ {feature}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mt-6 border-t border-slate-100 pt-4">
                  <div className="flex items-end justify-between gap-3">
                    <div>
                      <p className="text-xs text-slate-500">Mulai dari</p>
                      <p className="text-lg font-black text-[#07111f]">{car.price}</p>
                      <p className="text-[11px] text-slate-400">{car.priceLabel}</p>
                    </div>

                    <div className="flex flex-col gap-2">
                      <a
                        href={createWhatsAppUrl(car.name)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center rounded-xl bg-[#d4af37] px-4 py-2 text-center text-xs font-bold text-[#07111f] transition hover:bg-[#e4c45a]"
                      >
                        Booking
                      </a>
                      <a
                        href={createWhatsAppUrl(car.name)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-1 rounded-xl border border-emerald-500/40 bg-emerald-50/50 px-3 py-1.5 text-center text-xs font-bold text-emerald-600 transition hover:bg-emerald-500/10"
                      >
                        <MessageCircle size={13} />
                        WhatsApp
                      </a>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE AREA */}
      <section className="bg-[#0b1726] py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#e4c45a]">
              Cakupan Wilayah
            </p>

            <h2 className="mt-3 text-3xl font-bold text-white sm:text-4xl">
              Area Layanan Rental Mobil
              <span className="text-[#d4af37]"> Bekasi & Sekitarnya</span>
            </h2>

            <p className="mt-5 leading-8 text-slate-400">
              Kami melayani penjemputan di seluruh kawasan Bekasi Kota, Kabupaten Bekasi, 
              serta wilayah industri Cikarang.
            </p>
          </div>

          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {areaGroups.map((area) => (
              <article
                key={area.title}
                className="rounded-2xl border border-white/10 bg-white/[0.04] p-6"
              >
                <div className="flex items-center gap-2 text-[#e4c45a]">
                  <MapPin size={18} />
                  <h3 className="font-bold text-white">{area.title}</h3>
                </div>

                <p className="mt-3 text-sm leading-6 text-slate-400">
                  {area.description}
                </p>

                <ul className="mt-5 space-y-2.5">
                  {area.places.map((place) => (
                    <li
                      key={place}
                      className="border-b border-white/5 pb-2 text-sm text-slate-400 last:border-0"
                    >
                      {place}
                    </li>
                  ))}
                </ul>

                <a
                  href={createWhatsAppUrl(`Perjalanan area ${area.title}`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#e4c45a] transition hover:text-white"
                >
                  Tanyakan area ini
                  <ArrowRight size={15} />
                </a>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* AIRPORT */}
      <section className="overflow-hidden bg-[#07111f]">
        <div className="mx-auto grid max-w-7xl lg:grid-cols-2">
          <div className="relative min-h-[380px] lg:min-h-[520px]">
            <img
              src="/assets/images/Armada Hiace Nayvera.jpeg"
              alt="Armada NAYVERA untuk perjalanan bandara dari Bekasi"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#07111f]/50 to-transparent lg:bg-gradient-to-r lg:from-transparent lg:to-[#07111f]" />
          </div>

          <div className="flex items-center px-6 py-20 lg:px-12">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#e4c45a]">
                Airport Transfer & Drop Off
              </p>

              <h2 className="mt-3 text-3xl font-bold leading-tight text-white sm:text-4xl">
                Antar Jemput Bandara
                <span className="text-[#d4af37]"> Praktis dari Bekasi</span>
              </h2>

              <p className="mt-5 leading-8 text-slate-300">
                Perjalanan dari Bekasi menuju Bandara Soekarno-Hatta atau Halim Perdanakusuma 
                kini semakin nyaman. NAYVERA menyediakan layanan antar-jemput tepat waktu 
                dengan armada bersih dan pengemudi profesional.
              </p>

              <div className="mt-8">
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-xl bg-[#d4af37] px-6 py-3.5 font-semibold text-[#07111f] transition hover:bg-[#e4c45a]"
                >
                  <MessageCircle size={18} />
                  Pesan Antar Jemput Bandara
                  <ArrowRight size={17} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* UNIQUE SEO ARTICLES SECTION */}
      <section className="bg-slate-50 py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#b89528]">
              Wawasan & Panduan Sewa Mobil
            </p>
            <h2 className="mt-3 text-3xl font-bold text-[#07111f] sm:text-4xl">
              Artikel & Tips Seputar
              <span className="text-[#b89528]"> Rental Mobil di Bekasi</span>
            </h2>
            <p className="mt-4 text-slate-600">
              Informasi lengkap untuk membantu merencanakan perjalanan bisnis, industri, dan keluarga Anda di Bekasi.
            </p>
          </div>

          <div className="mt-12 grid gap-8 lg:grid-cols-2">
            {articles.map((art) => (
              <article
                key={art.number}
                className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm transition hover:border-[#d4af37]/60 hover:shadow-md"
              >
                <div className="flex items-center justify-between">
                  <span className="rounded-full bg-[#07111f] px-3.5 py-1 text-xs font-bold text-[#e4c45a]">
                    {art.category}
                  </span>
                  <span className="text-2xl font-black text-slate-300">
                    {art.number}
                  </span>
                </div>

                <h3 className="mt-5 text-xl font-bold text-[#07111f]">
                  {art.title}
                </h3>

                <div className="mt-4 space-y-3 text-sm leading-7 text-slate-600">
                  {art.paragraphs.map((p, idx) => (
                    <p key={idx}>{p}</p>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#b89528]">
              Kenapa Memilih Kami
            </p>
            <h2 className="mt-3 text-3xl font-bold text-[#07111f] sm:text-4xl">
              Alasan Percayakan Perjalanan Anda
              <span className="text-[#b89528]"> Kepada NAYVERA</span>
            </h2>
          </div>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {reasons.map((r) => {
              const Icon = r.icon;
              return (
                <div
                  key={r.title}
                  className="rounded-2xl border border-slate-200 p-6 transition hover:border-[#b89528]"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#07111f] text-[#e4c45a]">
                    <Icon size={22} />
                  </div>
                  <h3 className="mt-5 font-bold text-[#07111f]">{r.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-600">{r.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-slate-50 py-20">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#b89528]">
              FAQ
            </p>
            <h2 className="mt-3 text-3xl font-bold text-[#07111f] sm:text-4xl">
              Pertanyaan yang Sering Diajukan
            </h2>
            <p className="mt-4 text-slate-600">
              Temukan jawaban seputar layanan sewa mobil Bekasi bersama NAYVERA.
            </p>
          </div>

          <div className="mt-12 space-y-4">
            {faqs.map((faq, index) => (
              <details
                key={index}
                className="group rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition open:border-[#d4af37]"
              >
                <summary className="flex cursor-pointer items-center justify-between font-bold text-[#07111f]">
                  {faq.question}
                  <ChevronDown size={18} className="transition group-open:rotate-180 text-[#b89528]" />
                </summary>
                <p className="mt-4 text-sm leading-7 text-slate-600">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FOOTER BANNER */}
      <section className="bg-[#07111f] py-20 text-white">
        <div className="mx-auto max-w-5xl px-6 text-center lg:px-8">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#e4c45a]">
            Siap Berangkat dari Bekasi?
          </p>
          <h2 className="mt-3 text-3xl font-bold sm:text-4xl">
            Pesan Kendaraan Anda Sekarang di NAYVERA
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-slate-300">
            Dapatkan pelayanan cepat, ramah, dan transparan untuk setiap kebutuhan perjalanan Anda.
          </p>
          <div className="mt-8 flex justify-center gap-4">
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-xl bg-[#d4af37] px-8 py-4 font-bold text-[#07111f] transition hover:bg-[#e4c45a]"
            >
              <MessageCircle size={20} />
              Hubungi WhatsApp Sekarang
              <ArrowRight size={18} />
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
    </main>
  );
}